// Add Obligation Wizard — curated copy home (the recordPaymentCopy pattern).
// Explains the CREATION decision to a staff member: what slot the money
// collects through, who ends up with it, and what happens between "recorded"
// and "collected". Engine facts referenced here (B3 review → statement
// billing → WF1 obligation-capped collection, dormant P4-P6 recovery rows)
// are the PR-1 pipeline behaviors — the wizard itself never touches the
// waterfall.
//
// 🔴 EVERY sentence below that asserts billing, review, collection or a
// waterfall slot is TRUE ONLY of a borrower-payable entry. Any other payer
// produces a record-only tracker row that never enters the pipeline, so from
// the payer step (step 2) onward the copy is payer-aware: each such string is
// a function of `isBorrower`, not a constant. Step 1 runs BEFORE the payer is
// chosen and deliberately still describes the pipeline — that is the default
// path, and the step-2 warning is what corrects it.

// --- Steps ---------------------------------------------------------------

export const ADD_OBLIGATION_STEPS = [
  { key: "type", label: "What is it" },
  { key: "parties", label: "Who's involved" },
  { key: "collection", label: "How it collects" },
  { key: "amount", label: "Amount & date" },
  { key: "pool", label: "Revenue pool" },
  { key: "review", label: "Review & create" },
] as const;

export type AddObligationStepKey = (typeof ADD_OBLIGATION_STEPS)[number]["key"];

// --- Step 1 — type -----------------------------------------------------------

export const TYPE_STEP_INTRO =
  "Pick what this fee or cost is. Each option maps to a real slot in the loan's payment waterfall — the choice decides who gets paid and when.";

export const CUSTOM_OPTION_TITLE = "Custom / other";

export const CUSTOM_OPTION_BLURB =
  "Something not in the list. It still bills and collects — through the standard Phoenix fee slot — and flags the name for admin catalog review.";

export const CUSTOM_LABEL_HELP =
  "Name the fee the way it should read on the statement. It collects through the standard Phoenix fee slot (P3 — Late fee / direct); an admin reviews the name for the permanent catalog.";

// --- Step 2 — parties ---------------------------------------------------------

export const PARTIES_STEP_INTRO =
  "Who pays, and who the money is for. The waterfall resolves the actual payee from the slot — the pickers here record provenance on the fee row.";

export const PAYER_QUICK_OPTIONS = ["Borrower", "Seller", "Broker", "Phoenix"] as const;

export const NON_BORROWER_WARNING =
  "Only borrower-payable entries enter the billing pipeline. With this payer, the entry is recorded for tracking only — it will NOT go to B3 review, bill on a statement, or collect through the waterfall.";

export const NON_BORROWER_ACK_LABEL =
  "I understand this entry is record-only and will not collect.";

export function recipientSentence(
  description: string | null,
  isBorrower: boolean,
): string {
  if (!isBorrower) {
    return "Nothing collects on this entry, so no payee is resolved — the pickers below record who the cost sits with.";
  }
  return description
    ? `Where the money goes when it collects: ${description}`
    : "Where the money goes when it collects is resolved by the waterfall slot at payment time.";
}

// --- Step 3 — collection & lifecycle -------------------------------------------

export const COLLECTION_STEP_INTRO =
  "Where this lands in the loan's payment waterfall. Money flows top-down each payment — earlier rungs are paid before later ones.";

/** The ladder below is shown on every path, but on a record-only entry it is
 *  context, not a destination — nothing lands on it. */
export const COLLECTION_STEP_INTRO_RECORD_ONLY =
  "The loan's payment waterfall, for context. This entry doesn't enter it — with a payer other than the borrower it's recorded for tracking only, so no rung collects it.";

export function collectionStepIntro(isBorrower: boolean): string {
  return isBorrower ? COLLECTION_STEP_INTRO : COLLECTION_STEP_INTRO_RECORD_ONLY;
}

export const FIREABLE_NOTE =
  "This slot is live: once approved and billed, it collects on the loan's next payment run.";

export const DORMANT_NOTE =
  "This slot exists in the waterfall but its trigger is not wired yet — the entry is recorded and billed now; waterfall collection begins when the recovery-cost rows are activated (a scheduled follow-up).";

/** Replaces the fireable/dormant slot note entirely on a record-only entry:
 *  whether the slot's trigger is wired is beside the point when nothing is
 *  routed to it. */
export const RECORD_ONLY_SLOT_NOTE =
  "Whether this slot is live doesn't apply here — the entry is record-only, so it's never routed to a waterfall rung. The lifecycle below is recorded as context for whoever reads the row later.";

export const LIFECYCLE_OPTIONS = [
  {
    value: "one_time",
    label: "One-time",
    blurb: "A single charge — the normal case.",
  },
  {
    value: "event_triggered",
    label: "Event-triggered",
    blurb: "Tied to a specific event (an inspection, a bounced check…). Note the event below.",
  },
  {
    value: "recurring",
    label: "Recurring",
    blurb:
      "Expected to repeat. Each occurrence is still entered (and reviewed) individually — this records the intent.",
  },
] as const;

export type LifecycleValue = (typeof LIFECYCLE_OPTIONS)[number]["value"];

// --- Step 4 — amount & date -----------------------------------------------------

export const FUTURE_DATE_ERROR = "The incurred date can't be in the future.";

export function duplicateWarning(
  count: number,
  typeLabel: string,
  isBorrower: boolean,
): string {
  const noun = count === 1 ? "entry" : "entries";
  const consequence = isBorrower
    ? "Adding another bills the borrower again"
    : "Adding another records the same cost twice";
  return `This loan already has ${count} open ${typeLabel} ${noun}. ${consequence} — make sure this isn't a duplicate.`;
}

export const DUPLICATE_ACK_LABEL = "This is a separate charge, not a duplicate.";

export const LARGE_AMOUNT_WARNING =
  "That's an unusually large fee. Double-check the amount before creating it.";

// --- Step 5 — revenue pool -------------------------------------------------------

export const POOL_STEP_INTRO =
  "Which Phoenix revenue pool the collected money reports under. The type's default is usually right — reporting only, it never changes who gets paid.";

export const POOL_STEP_INTRO_RECORD_ONLY =
  "Which Phoenix revenue pool to tag this cost with for reporting. Nothing collects on a record-only entry, so the tag is a label on the row — it never changes who gets paid.";

export function poolStepIntro(isBorrower: boolean): string {
  return isBorrower ? POOL_STEP_INTRO : POOL_STEP_INTRO_RECORD_ONLY;
}

export function poolDepartureNote(defaultLabel: string): string {
  return `The default pool for this type is ${defaultLabel}. A different pool is allowed — it changes reporting classification only.`;
}

// --- Step 6 — review --------------------------------------------------------------

/** What the review grid shows for a row that only describes COLLECTION — the
 *  waterfall slot and the payment channel — when nothing will collect. Saying
 *  "P3 — Late fee" or "Phoenix direct — from borrower" there states the
 *  opposite of what the warning two steps earlier made the staff member
 *  acknowledge. */
export const REVIEW_NOT_APPLICABLE = "Not applicable — record-only";

/** Beneficiary fallback. On the borrower path the waterfall resolves the payee
 *  at payment time; on a record-only row there is no payment to resolve one
 *  at. */
export const REVIEW_BENEFICIARY_RECORD_ONLY = "Not recorded";

export const PIPELINE_SENTENCE =
  "Creating this sends it to B3 fee review. Once approved it becomes a waterfall obligation, bills on the borrower's next statement, and collects through the waterfall.";

export const RECORD_ONLY_SENTENCE =
  "Creating this records it for tracking only — the payer isn't the borrower, so it never bills or collects through the pipeline.";

export function friendlyServerError(message: string): string {
  return message || "The entry could not be created.";
}
